import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLine } from "./Motion";

const ease = [0.22, 1, 0.36, 1];

const HeroFrame = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });
  const light = useTransform([mx, my], ([x, y]) =>
    `radial-gradient(420px circle at ${x * 100}% ${y * 100}%, rgba(255,236,210,.38), transparent 60%)`);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <div ref={ref} className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.5, ease, delay: 0.5 }}
        >
        <div className="roof-clip relative aspect-[4/5] w-full overflow-hidden bg-[#2A251F]" data-testid="hero-image-frame">
          <motion.img
            src="/img/hero.jpg"
            alt="Bright remodeled kitchen with warm wood island"
            style={{ y: imgY, scale: 1.18 }}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714]/55 via-transparent to-transparent" />
          <motion.div className="absolute inset-0 mix-blend-soft-light" style={{ background: light }} />
        </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, ease, delay: 1.4 }}
          style={{ z: 60 }}
          className="absolute left-3 sm:-left-10 bottom-10 rounded-2xl border border-[#E2DDD5] bg-[#F8F6F0]/90 backdrop-blur-xl px-5 py-4 shadow-[0_30px_60px_-30px_rgba(30,27,24,.5)]">
          <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#605A52]">Family-owned &amp; operated</p>
          <p className="font-serif-d text-2xl font-semibold mt-1">Measured twice.<span className="italic text-[#B04E2A]"> Built once.</span></p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" className="grain relative min-h-[100svh] pt-[110px] pb-16 lg:pb-24" data-testid="hero-section">
    <div aria-hidden className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#E2DDD5_1px,transparent_1px)] bg-[size:calc(100%/6)_100%] opacity-50" />
    <div className="relative mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12">
      <div className="lg:col-span-7 flex flex-col justify-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
          className="font-mono-d text-xs uppercase tracking-[0.25em] text-[#B04E2A] flex items-center gap-3">
          <span className="h-px w-8 bg-[#B04E2A]" /> Remodeling · Repairs · Craft
        </motion.p>
        <h1 className="mt-6 font-serif-d font-medium tracking-[-0.02em] leading-[0.92] text-[3.4rem] sm:text-7xl lg:text-[6.4rem] xl:text-[7.4rem]" data-testid="hero-heading">
          <MaskLine delay={0.35}>Built by family.</MaskLine>
          <MaskLine delay={0.5}><span className="italic text-[#B04E2A]">Made to last</span></MaskLine>
          <MaskLine delay={0.65}>for yours.</MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#605A52] leading-relaxed">
          James Family Home Improvement LLC brings honest workmanship to kitchens, baths, decks, floors and every fix in between — handled
          like it's our own home.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9, ease }}
          className="mt-10 flex flex-wrap items-center gap-4">
          <a href="#quote" data-testid="hero-cta-quote-button"
            className="group inline-flex items-center gap-3 rounded-full bg-[#C85A32] pl-7 pr-2 py-2 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#1E1B18]">
            Request a Free Quote
            <span className="grid h-10 w-10 place-items-center rounded-full bg-white/15 transition-transform duration-500 group-hover:rotate-45">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </a>
          <a href="#projects" data-testid="hero-cta-projects-button"
            className="group inline-flex items-center gap-2 rounded-full border border-[#1E1B18]/25 px-6 py-4 text-sm font-semibold transition-colors duration-300 hover:border-[#1E1B18] hover:bg-[#1E1B18] hover:text-[#F8F6F0]">
            See Our Work <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
        </motion.div>
      </div>
      <div className="lg:col-span-5 lg:pt-6">
        <HeroFrame />
      </div>
    </div>
  </section>
);
